import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Flag, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { shortDate, relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { RenovationMilestoneDto } from '@cc/types';

interface Props {
  projectId: string;
  milestones: RenovationMilestoneDto[];
}

/**
 * Strip of upcoming milestones. Shows the next 3 by planned date. Inline form
 * to add a new one. Click a milestone row to mark complete or delete.
 */
export function MilestoneStrip({ projectId, milestones }: Props) {
  const [adding, setAdding] = useState(false);

  const upcoming = milestones
    .filter((m) => m.status !== 'COMPLETE')
    .slice(0, 5);

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-medium">
            <Flag className="h-3.5 w-3.5" />
            Milestones
          </h3>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAdding((v) => !v)}
            title="Add milestone"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>

        {adding && <AddMilestoneForm projectId={projectId} onDone={() => setAdding(false)} />}

        {upcoming.length === 0 ? (
          <p className="text-xs italic text-muted-foreground">
            No upcoming milestones. Add one above (e.g. "demolition complete", "occupancy").
          </p>
        ) : (
          <ul className="space-y-1.5">
            {upcoming.map((m) => (
              <MilestoneRow key={m.id} projectId={projectId} milestone={m} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AddMilestoneForm({
  projectId,
  onDone,
}: {
  projectId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [date, setDate] = useState('');

  const create = useMutation({
    mutationFn: () => api.createMilestone(projectId, { name, plannedDate: date }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.renovation(projectId) });
      toast.success('Milestone added.');
      onDone();
    },
    onError: () => {
      toast.error('Failed to add milestone.');
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim() || !date) return;
        create.mutate();
      }}
      className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border p-2"
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Milestone name"
        className="h-8 min-w-[180px] flex-1 text-sm"
      />
      <Input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className="h-8 w-[150px] text-sm"
      />
      <Button size="sm" type="submit" disabled={!name.trim() || !date || create.isPending}>
        Add
      </Button>
      <Button size="sm" variant="ghost" type="button" onClick={onDone}>
        <X className="h-3.5 w-3.5" />
      </Button>
    </form>
  );
}

function MilestoneRow({
  projectId,
  milestone,
}: {
  projectId: string;
  milestone: RenovationMilestoneDto;
}) {
  const queryClient = useQueryClient();
  const todayIso = new Date().toISOString().slice(0, 10);
  const isMissed = milestone.plannedDate < todayIso && !milestone.actualDate;
  const isThisWeek = !isMissed && milestone.plannedDate <= addDaysIso(todayIso, 7);

  const markComplete = useMutation({
    mutationFn: () =>
      api.updateMilestone(projectId, milestone.id, {
        actualDate: todayIso,
        status: 'COMPLETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.renovation(projectId) });
      toast.success('Marked complete.');
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteMilestone(projectId, milestone.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.renovation(projectId) });
    },
  });

  return (
    <li
      className={cn(
        'flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1.5 text-sm',
        isMissed && 'border-rose-500/40 bg-rose-500/5',
        isThisWeek && !isMissed && 'border-amber-500/40 bg-amber-500/5',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{milestone.name}</p>
        <p className="text-[11px] text-muted-foreground">
          {shortDate(milestone.plannedDate)} · {relativeTime(milestone.plannedDate)}
        </p>
      </div>
      <div className="flex items-center gap-1">
        {isMissed && <Badge variant="danger">missed</Badge>}
        {isThisWeek && !isMissed && <Badge variant="warning">soon</Badge>}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => markComplete.mutate()}
          disabled={markComplete.isPending}
          title="Mark complete"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            if (confirm(`Delete milestone "${milestone.name}"?`)) remove.mutate();
          }}
          disabled={remove.isPending}
          title="Delete"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </li>
  );
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
